// ✅ src/pages/VehicleV2/PeriodicEntryReport.jsx
// UPDATED IN JOB CARD ENTRY DETAIL REPORT STYLE
//
// Uses: app.use("/api/periodicEntryReport", periodicEntryReport);
//
// API used:
//  1) GET  /api/periodicEntryReport/init?branchId=8
//  2) GET  /api/periodicEntryReport/list?branchtype=HO&branchid=8&userid=31&fromDate=...&toDate=...&branch=...&store=...&status=...&vehicleStatus=...&vehicleNo=...&vehicleCode=...&vehicleMainType=...
//  3) POST /api/periodicEntryReport/print/list
//  4) GET  /api/periodicEntryReport/print/row/:vehicleid/:maintenanceid
//
// ✅ Updated pattern similar to JobCardEntryDetailReport
// ✅ common localStorage / sessionStorage header pattern
// ✅ useMemo + useCallback pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / reset / refresh / pagination / print flow

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaEye,
  FaSyncAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/periodicEntryReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const createDefaultFilters = (branchType, branchId) => ({
  fromDate: "",
  toDate: "",
  branch: branchType === "HO" ? "Select" : String(branchId),
  store: "Select",
  status: "Select",
  vehicleStatus: "Select",
  vehicleNo: "",
  vehicleCode: "",
  vehicleMainType: "Select",
});

export default function PeriodicEntryReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchtype: toStr(branchType || "HO"),
      branchid: sessionBranchId,
      userid: toInt(loginUserId, 0),
    }),
    [branchType, sessionBranchId, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchtype, session.branchid),
    [session.branchtype, session.branchid]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     pagination
  =========================== */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ===========================
     dropdown states
  =========================== */
  const [ddl, setDdl] = useState({
    branches: [],
    stores: [],
    vehicleMainTypes: [],
  });

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);

  /* ===========================
     computed
  =========================== */
  const totalRows = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / itemsPerPage)),
    [totalRows]
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirst, indexOfLast),
    [rows, indexOfFirst, indexOfLast]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  /* ===========================
     helpers
  =========================== */
  const getAlertClass = useCallback((alertText) => {
    const a = toStr(alertText).toLowerCase();
    if (a.includes("expired")) return "bg-red-100 text-red-800";
    if (a.includes("nearly")) return "bg-yellow-100 text-yellow-800";
    return "bg-green-100 text-green-800";
  }, []);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* ===========================
     dropdown loaders
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const res = await axios.get(`${API_BASE}/init`, {
        params: { branchId: session.branchid || 0 },
        headers: commonHeaders,
      });

      const payload = res.data || {};
      if (!payload.success) {
        throw new Error(payload.message || "Init failed");
      }

      setDdl({
        branches: toList(payload.branches),
        stores: toList(payload.stores),
        vehicleMainTypes: toList(payload.vehicleMainTypes),
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load dropdowns"
      );

      setDdl({
        branches: [],
        stores: [],
        vehicleMainTypes: [],
      });
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchid, commonHeaders]);

  /* ===========================
     list loader
  =========================== */
  const loadList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);

        const params = {
          branchtype: session.branchtype,
          branchid: session.branchid,
          userid: session.userid,

          fromDate: nextFilters.fromDate || "",
          toDate: nextFilters.toDate || "",
          branch:
            session.branchtype === "HO"
              ? nextFilters.branch || "Select"
              : String(session.branchid),
          store: nextFilters.store || "Select",
          status: nextFilters.status || "Select",
          vehicleStatus: nextFilters.vehicleStatus || "Select",
          vehicleNo: nextFilters.vehicleNo || "",
          vehicleCode: nextFilters.vehicleCode || "",
          vehicleMainType: nextFilters.vehicleMainType || "Select",
        };

        const res = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        const payload = res.data || {};
        if (!payload.success) {
          throw new Error(payload.message || "List failed");
        }

        const list = toList(payload.rows);
        setRows(list);
        setCurrentPage(1);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load report"
        );
        setRows([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setRows([]);
      setCurrentPage(1);
      await loadInit();
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadInit]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setField(name, value);
    },
    [setField]
  );

  const handleSearch = useCallback(async () => {
    await loadList(filters);
    toast.success("Report loaded");
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchtype, session.branchid);
    setFilters(reset);
    await loadList(reset);
    toast.info("Showing all records");
  }, [session.branchtype, session.branchid, loadList]);

  const handleClearFilters = useCallback(async () => {
    const reset = createDefaultFilters(session.branchtype, session.branchid);
    setFilters(reset);
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchtype, session.branchid]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await loadList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, loadList, defaultFilters]);

  const handlePrintList = useCallback(() => {
    try {
      const payload = {
        session,
        filters,
        rows,
        printedAt: new Date().toISOString(),
      };

      localStorage.setItem("PERIODIC_ENTRY_PRINT_LIST", JSON.stringify(payload));

      window.open(
        "/vehicle/PeriodicENtryReport_AllPrint",
        "_blank",
        "width=1100,height=800,scrollbars=yes"
      );
      toast.info("Printing all records");
    } catch (e) {
      console.error(e);
      toast.error("Print failed");
    }
  }, [session, filters, rows]);

  const handlePrintRow = useCallback((r) => {
    const vehicleid = r?.fld_vehicleid || 0;
    const maintenanceid = r?.fld_maintenanceid || 0;

    window.open(
      `/vehicle/PeriodicEntryReport_Print?vehicleid=${vehicleid}&maintenanceid=${maintenanceid}`,
      "_blank",
      "width=1100,height=800,scrollbars=yes"
    );
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const paginate = useCallback((p) => {
    setCurrentPage(p);
  }, []);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
      await loadList(defaultFilters);
    })();
  }, [loadPage, loadList, defaultFilters]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PERIODIC ENTRY REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Periodic Entry Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchid || "-"} {" • "} User ID: {session.userid || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {totalRows}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isBusy
                      ? isDark
                        ? "bg-yellow-900 text-yellow-100"
                        : "bg-yellow-100 text-yellow-800"
                      : isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {pageLoading
                    ? "Loading Page"
                    : dropdownLoading
                    ? "Loading Dropdowns"
                    : listLoading
                    ? "Loading List"
                    : "Ready"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter />
                    FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date</label>
                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branch"
                        value={filters.branch}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || session.branchtype !== "HO"}
                      >
                        <option value="Select">Select</option>
                        {ddl.branches.map((b, i) => (
                          <option
                            key={`${b.fld_branchid || i}-${i}`}
                            value={String(b.fld_branchid || "Select")}
                          >
                            {b.fld_BranchName || b.branchName || "Select"}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Store</label>
                      <select
                        name="store"
                        value={filters.store}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {ddl.stores.map((s, i) => (
                          <option
                            key={`${s.fld_storeid || i}-${i}`}
                            value={String(s.fld_storeid || "Select")}
                          >
                            {s.fld_storename || s.storeName || "Select"}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Status</label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="ServiceNearly">Service Nearly</option>
                        <option value="ServiceExpired">Service Expired</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Status</label>
                      <select
                        name="vehicleStatus"
                        value={filters.vehicleStatus}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="All">All</option>
                        <option value="Running">Running</option>
                        <option value="Stopped">Stopped</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Number</label>
                      <input
                        type="text"
                        name="vehicleNo"
                        value={filters.vehicleNo}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle number"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle code"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select
                        name="vehicleMainType"
                        value={filters.vehicleMainType}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {ddl.vehicleMainTypes.map((v, i) => {
                          const label =
                            v.fld_vehicletypename ||
                            v.fld_vehicle_subtypename ||
                            v.VehicleType ||
                            v.name ||
                            Object.values(v || {})[0] ||
                            "Type";

                          const value =
                            v.fld_vehicletypeindex ||
                            v.fld_vehicletypename ||
                            v.fld_vehicle_subtypename ||
                            label;

                          return (
                            <option key={`${value}-${i}`} value={String(value)}>
                              {String(label)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-3 flex-wrap justify-end">
                      <button
                        onClick={handleClearFilters}
                        type="button"
                        disabled={isBusy}
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        Search Records
                      </button>

                      <button
                        onClick={handlePrintList}
                        type="button"
                        disabled={isBusy || rows.length === 0}
                        className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy || rows.length === 0
                            ? "bg-gray-400 cursor-not-allowed text-white"
                            : "bg-indigo-600 hover:bg-indigo-700 text-white"
                        }`}
                      >
                        <FaPrint />
                        Print List
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {currentItems.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Records Found
                    </h3>
                    <p
                      className={`mb-6 max-w-sm mx-auto ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Try adjusting your search criteria or click "View All".
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className="px-4 py-4 text-center font-semibold">Branch</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Reg No</th>
                          <th className="px-4 py-4 text-center font-semibold">SubAssembly</th>
                          <th className="px-4 py-4 text-center font-semibold">Description</th>
                          <th className="px-4 py-4 text-center font-semibold">Last Changed</th>
                          <th className="px-4 py-4 text-center font-semibold">Usage Limit</th>
                          <th className="px-4 py-4 text-center font-semibold">Next Service</th>
                          <th className="px-4 py-4 text-center font-semibold">Current Cumulative</th>
                          <th className="px-4 py-4 text-center font-semibold">Update Date</th>
                          <th className="px-4 py-4 text-center font-semibold">Remaining</th>
                          <th className="px-4 py-4 text-center font-semibold">Alert</th>
                          <th className="px-4 py-4 text-center font-semibold">Actions</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {currentItems.map((r, idx) => {
                          const branchName = r.fld_branchname || r.branch || "";
                          const vType = r.VehicleType || r.fld_vehicletypename || r.mainType || "";
                          const vCode = r.fld_vehicle_code || r.code || "";
                          const vName = r.fld_vehicle_name || r.name || "";
                          const regNo = r.fld_vehicle_regno || r.regNo || "";
                          const subAsm = r.fld_vehicle_subassemblyname || r.subAssembly || "";
                          const desc = r.fld_Description || r.description || "";
                          const lastChanged = r.fld_hrmtrreading ?? r.lastChanged ?? "";
                          const usageLimit = r.fld_usagelimit_calc ?? r.fld_usagelimit ?? r.usageLimit ?? "";
                          const nextService = r.fld_usagelimit_calc ?? r.nextService ?? "";
                          const cum = r.fld_hrskm ?? r.currentCumulative ?? "";
                          const updateDate = r.fld_progressentrydate || r.fld_curdate || r.updateDate || "";
                          const remaining = r.fld_Remaininghr ?? r.remaining ?? "";
                          const alertText = r.alertText || r.alert || "Normal";

                          return (
                            <tr
                              key={r.fld_vehicleid ? `${r.fld_vehicleid}-${r.fld_maintenanceid}-${idx}` : idx}
                              className={`${
                                isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                              } transition-colors duration-200`}
                            >
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {branchName}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {vType}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {vCode}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {vName}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {regNo}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {subAsm}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {desc}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {fmtNum(lastChanged)}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {fmtNum(usageLimit)}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {fmtNum(nextService)}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {fmtNum(cum)}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {updateDate}
                              </td>
                              <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                                {Number(remaining) > 0 ? (
                                  <span className="text-green-600">+{fmtNum(remaining)}</span>
                                ) : (
                                  <span className="text-red-600">{fmtNum(remaining)}</span>
                                )}
                              </td>
                              <td className="px-4 py-4 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertClass(alertText)}`}>
                                  {alertText}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handlePrintRow(r)}
                                  disabled={isBusy}
                                  className={`p-2 rounded ${
                                    isDark
                                      ? "bg-gray-700 text-white hover:bg-gray-600"
                                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title="Print"
                                  type="button"
                                >
                                  <FaPrint className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {rows.length > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                  <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Showing {indexOfFirst + 1} to {Math.min(indexOfLast, rows.length)} of {rows.length} entries
                  </div>

                  <nav className="flex gap-1">
                    <button
                      onClick={() => paginate(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1 || isBusy}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === 1 || isBusy
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                      type="button"
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === pageNum
                              ? `${buttonClass} text-white`
                              : isDark
                              ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                          type="button"
                          disabled={isBusy}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages || isBusy}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === totalPages || isBusy
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                      type="button"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}